import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import api from "@/utils/api";
import { toast } from "sonner";
import {
  Wallet as WalletIcon, Plus, Minus, Search, Loader2, ShieldOff, Shield, AlertCircle,
} from "lucide-react";

const TX_TYPE_BADGE = {
  TOPUP: "bg-emerald-100 text-emerald-700 border-emerald-200",
  SHIPMENT: "bg-blue-100 text-blue-700 border-blue-200",
  REFUND: "bg-amber-100 text-amber-700 border-amber-200",
  MANUAL: "bg-purple-100 text-purple-700 border-purple-200",
  PAYMENT: "bg-slate-200 text-slate-700 border-slate-300",
  ADJUSTMENT: "bg-slate-200 text-slate-700 border-slate-300",
};

export default function AdminWalletDashboard() {
  const navigate = useNavigate();
  const [custQ, setCustQ] = useState("");
  const [custOpen, setCustOpen] = useState(false);
  const [custOptions, setCustOptions] = useState([]);
  const [allCustomers, setAllCustomers] = useState([]);
  const [allCustomersLoading, setAllCustomersLoading] = useState(true);
  const [customer, setCustomer] = useState(null);

  const [summary, setSummary] = useState(null);
  const [txs, setTxs] = useState([]);
  const [loading, setLoading] = useState(false);

  // Modals
  const [creditOpen, setCreditOpen] = useState(false);
  const [debitOpen, setDebitOpen] = useState(false);
  const [limitOpen, setLimitOpen] = useState(false);
  const [form, setForm] = useState({ amount: "", reason: "", reference: "" });
  const [newLimit, setNewLimit] = useState("");

  useEffect(() => {
    if (!custOpen) return;
    const t = setTimeout(async () => {
      try {
        const r = await api.get(
          `/v2/customers/profiles?search=${encodeURIComponent(custQ)}`
        );
        setCustOptions(r.data?.items || []);
      } catch {
        setCustOptions([]);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [custQ, custOpen]);

    useEffect(() => {
    const loadAll = async () => {
      setAllCustomersLoading(true);
      try {
        const r = await api.get(`/v2/customers/profiles?page_size=100`);
        setAllCustomers(r.data?.items || []);
      } catch {
        setAllCustomers([]);
      } finally {
        setAllCustomersLoading(false);
      }
    };
    loadAll();
  }, []);

  const loadCustomer = useCallback(async (id) => {
    setLoading(true);
    try {
      const [s, t] = await Promise.all([
        api.get(`/v2/wallets/${id}/summary`),
        api
          .get(`/v2/wallet-ledger-v2/${id}?page_size=200`)
          .catch(() => ({ data: { transactions: [] } })),
      ]);
      setSummary(s.data);
      // wallet-ledger-v2 returns {transactions: [...]} with debit/credit fields
      // (plus a few legacy entries with amount/type from before the Aug 18
      // schema fix) -- map both shapes into the flat {type, amount, ...}
      // shape this dashboard's table already renders.
      const rawTxs = t.data?.transactions || [];
      const mappedTxs = rawTxs.map((tx) => ({
        id: tx.stx_id || tx._id,
        type: tx.transaction_type || (tx.type ? tx.type.toUpperCase() : "OTHER"),
        amount: tx.debit != null || tx.credit != null
          ? (tx.credit || 0) - (tx.debit || 0)
          : (tx.amount || 0),
        created_at: tx.created_at,
        description: tx.description,
        reference: tx.awb_lrn || tx.order_id || tx.shipment_id,
        balance_after: tx.balance_after,
      }));
      setTxs(mappedTxs);
    } catch (e) {
      toast.error("Failed to load wallet");
    } finally {
      setLoading(false);
    }
  }, []);

  const selectCustomer = async (c) => {
    setCustomer(c);
    setCustOpen(false);
    setCustQ(c.company_name || c.contact_person || "");
    await loadCustomer(c.customer_id);
  };

  const submitCredit = async () => {
    const amt = Number(form.amount);
    if (!amt || !form.reason.trim()) {
      toast.error("Amount and reason required");
      return;
    }
    try {
      await api.post(`/v2/wallets/${customer.customer_id}/manual-credit`, {
        amount: amt,
        reason: form.reason,
        reference: form.reference,
      });
      toast.success("Credited");
      setCreditOpen(false);
      setForm({ amount: "", reason: "", reference: "" });
      loadCustomer(customer.customer_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const submitDebit = async () => {
    const amt = Number(form.amount);
    if (!amt || !form.reason.trim()) {
      toast.error("Amount and reason required");
      return;
    }
    if (!window.confirm(`Debit ₹${amt} from ${customer.company_name}?`)) return;
    try {
      await api.post(`/v2/wallets/${customer.customer_id}/manual-debit`, {
        amount: amt,
        reason: form.reason,
        reference: form.reference,
      });
      toast.success("Debited");
      setDebitOpen(false);
      setForm({ amount: "", reason: "", reference: "" });
      loadCustomer(customer.customer_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const toggleCreditWallet = async () => {
    if (!customer) return;
    const next = !(summary?.credit_wallet_enabled !== false);
    if (!window.confirm(`Set credit wallet to ${next ? "ENABLED" : "DISABLED"}?`)) return;
    try {
      await api.put(`/v2/wallets/${customer.customer_id}/credit-toggle`, {
        enabled: next,
      });
      toast.success(`Credit wallet ${next ? "enabled" : "disabled"}`);
      loadCustomer(customer.customer_id);
    } catch (e) {
      // try alternate endpoint shape
      try {
        await api.put(`/v2/wallets/${customer.customer_id}/credit-toggle?enabled=${next}`);
        toast.success(`Credit wallet ${next ? "enabled" : "disabled"}`);
        loadCustomer(customer.customer_id);
      } catch (e2) {
        toast.error(e2.response?.data?.detail || "Failed");
      }
    }
  };

  const submitLimit = async () => {
    const amt = Number(newLimit);
    if (!amt && amt !== 0) return toast.error("Enter limit");
    try {
      await api.put(`/v2/wallets/${customer.customer_id}/credit-limit`, {
        credit_limit: amt,
      });
      toast.success("Credit limit updated");
      setLimitOpen(false);
      setNewLimit("");
      loadCustomer(customer.customer_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const balance = Number(summary?.current_balance || 0);
  const isCredit = summary?.wallet_type === "CREDIT";
  const creditEnabled = summary?.credit_wallet_enabled !== false;

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="admin-wallet-dashboard">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Wallet Dashboard</h1>
            <p className="text-sm text-slate-500">
              View any customer&apos;s wallet, post manual entries, manage credit
            </p>
          </div>
          <Button
            onClick={() => navigate("/admin/wallet/topup")}
            className="bg-slate-800 hover:bg-slate-900 text-white"
          >
            <Plus className="w-4 h-4 mr-1" /> Top-up Customer
          </Button>
        </div>

        {/* Customer search */}
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={custQ}
              onFocus={() => setCustOpen(true)}
              onChange={(e) => {
                setCustQ(e.target.value);
                setCustOpen(true);
                setCustomer(null);
              }}
              placeholder="Search customer by name, mobile, email, GSTIN…"
              className="pl-8"
              data-testid="customer-search-input"
            />
            {custOpen && custOptions.length > 0 && (
              <div className="absolute top-11 left-0 right-0 max-h-64 overflow-auto bg-white border border-slate-200 rounded-md shadow-lg z-10">
                {custOptions.map((c) => (
                  <button
                    key={c.customer_id}
                    onClick={() => selectCustomer(c)}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 border-b last:border-b-0"
                    data-testid={`cust-option-${c.customer_id}`}
                  >
                    <div className="font-semibold">{c.company_name || c.contact_person}</div>
                    <div className="text-xs text-slate-500">
                      {c.email} {c.mobile && `· ${c.mobile}`}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
          {!customer && (
            <div className="mt-4 bg-white border border-slate-200 rounded-md">
              <div className="px-3 py-2 border-b bg-slate-50 text-xs font-semibold text-slate-500 uppercase">
                All Customers
              </div>
              {allCustomersLoading ? (
                <div className="px-3 py-6 text-center text-sm text-slate-400">Loading...</div>
              ) : allCustomers.length === 0 ? (
                <div className="px-3 py-6 text-center text-sm text-slate-400">No customers found</div>
              ) : (
                <div className="max-h-96 overflow-auto">
                  {allCustomers.map((c) => (
                    <button
                      key={c.customer_id}
                      onClick={() => selectCustomer(c)}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 border-b last:border-b-0"
                      data-testid={`all-cust-${c.customer_id}`}
                    >
                      <div className="font-semibold">{c.company_name || c.contact_person}</div>
                      <div className="text-xs text-slate-500">
                        {c.email} {c.mobile && `· ${c.mobile}`}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {customer && summary && (
          <>
            {/* Stat cards + actions */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <Stat label="Current Balance" value={`₹${balance.toFixed(2)}`} color={balance >= 0 ? "emerald" : "red"} big />
              <Stat label="Total Topped Up" value={`₹${Number(summary.total_credit || 0).toFixed(2)}`} color="blue" />
              <Stat label="Total Spent" value={`₹${Number(summary.total_debit || 0).toFixed(2)}`} color="slate" />
              <Stat label="Pending Invoices" value={`₹${Number(summary.pending_invoices_amount || 0).toFixed(2)}`} color="amber" />
            </div>

            {/* Admin actions */}
            <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-wrap gap-2">
              <Button
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={() => setCreditOpen(true)}
                data-testid="manual-credit-btn"
              >
                <Plus className="w-4 h-4 mr-1" /> Manual Credit
              </Button>
              <Button
                variant="outline"
                className="border-red-300 text-red-700"
                onClick={() => setDebitOpen(true)}
                data-testid="manual-debit-btn"
              >
                <Minus className="w-4 h-4 mr-1" /> Manual Debit
              </Button>
              {isCredit && (
                <>
                  <Button
                    variant="outline"
                    onClick={toggleCreditWallet}
                    data-testid="toggle-credit-wallet-btn"
                  >
                    {creditEnabled ? (
                      <>
                        <ShieldOff className="w-4 h-4 mr-1 text-red-600" /> Disable Credit Wallet
                      </>
                    ) : (
                      <>
                        <Shield className="w-4 h-4 mr-1 text-emerald-600" /> Enable Credit Wallet
                      </>
                    )}
                  </Button>
                  <Button variant="outline" onClick={() => { setNewLimit(String(summary.credit_limit || "")); setLimitOpen(true); }}
                    data-testid="set-credit-limit-btn">
                    Set Credit Limit
                  </Button>
                </>
              )}
              <div className="flex-1" />
              <span className={`inline-flex items-center text-xs px-3 py-1.5 rounded border ${
                creditEnabled ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-red-50 text-red-700 border-red-200"
              }`} data-testid="credit-status-badge">
                Credit wallet: {creditEnabled ? "ENABLED" : "DISABLED"}
              </span>
            </div>

            {/* Tx history */}
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="p-3 border-b border-slate-100 font-semibold text-sm">
                Transaction History
              </div>
              {loading ? (
                <div className="py-12 flex items-center justify-center text-slate-400">
                  <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
                </div>
              ) : txs.length === 0 ? (
                <div className="py-12 text-center text-slate-400">
                  <WalletIcon className="w-10 h-10 mx-auto mb-2 opacity-60" />
                  <p className="text-sm">No transactions</p>
                </div>
              ) : (
                <table className="w-full text-sm" data-testid="admin-tx-table">
                  <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                    <tr>
                      <th className="text-left p-3">Date</th>
                      <th className="text-left p-3">Type</th>
                      <th className="text-left p-3">Description</th>
                      <th className="text-left p-3">Reference</th>
                      <th className="text-right p-3">Amount</th>
                      <th className="text-right p-3">Balance</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {txs.map((tx, idx) => {
                      const t = (tx.type || tx.txn_type || "OTHER").toUpperCase();
                      const isCr = Number(tx.amount || 0) > 0;
                      const badgeKey = Object.keys(TX_TYPE_BADGE).find((k) => t.startsWith(k));
                      return (
                        <tr key={tx.id || idx} className="hover:bg-slate-50">
                          <td className="p-3 text-xs text-slate-600">
                            {new Date(tx.created_at || Date.now()).toLocaleString()}
                          </td>
                          <td className="p-3">
                            <span className={`inline-block text-[10px] font-bold px-2 py-0.5 rounded border ${TX_TYPE_BADGE[badgeKey] || "bg-slate-100 text-slate-600 border-slate-200"}`}>
                              {t}
                            </span>
                          </td>
                          <td className="p-3 text-xs">{tx.description || tx.reason || "—"}</td>
                          <td className="p-3 text-xs font-mono">{tx.reference || tx.txn_id || "—"}</td>
                          <td className={`p-3 text-right font-semibold ${isCr ? "text-emerald-700" : "text-red-700"}`}>
                            {isCr ? "+" : ""}₹{Math.abs(Number(tx.amount || 0)).toFixed(2)}
                          </td>
                          <td className="p-3 text-right text-slate-600 text-xs">
                            {tx.balance_after != null ? `₹${Number(tx.balance_after).toFixed(2)}` : "—"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}

        {!customer && (
          <div className="bg-white border border-dashed border-slate-300 rounded-xl py-16 text-center text-slate-400">
            <Search className="w-10 h-10 mx-auto mb-2 opacity-60" />
            <p className="text-sm">Select a customer to view wallet</p>
          </div>
        )}
      </div>

      {/* Manual Credit modal */}
      {creditOpen && (
        <ManualModal
          title="Manual Credit"
          color="emerald"
          form={form}
          setForm={setForm}
          onClose={() => setCreditOpen(false)}
          onSubmit={submitCredit}
        />
      )}
      {debitOpen && (
        <ManualModal
          title="Manual Debit"
          color="red"
          form={form}
          setForm={setForm}
          onClose={() => setDebitOpen(false)}
          onSubmit={submitDebit}
          warn="This will reduce the customer's balance. Confirm only if intentional."
        />
      )}
      {limitOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setLimitOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl p-5 w-full max-w-md shadow-2xl">
            <h3 className="text-lg font-bold text-slate-800 mb-3">Set Credit Limit</h3>
            <Input type="number" value={newLimit} onChange={(e) => setNewLimit(e.target.value)} placeholder="e.g. 50000" data-testid="credit-limit-input" />
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setLimitOpen(false)}>Cancel</Button>
              <Button onClick={submitLimit} className="bg-slate-800 hover:bg-slate-900 text-white" data-testid="credit-limit-submit">Update</Button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}

function Stat({ label, value, color = "slate", big }) {
  const c = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
    red: "bg-red-50 border-red-200 text-red-700",
    blue: "bg-blue-50 border-blue-200 text-blue-700",
    amber: "bg-amber-50 border-amber-200 text-amber-700",
    slate: "bg-slate-50 border-slate-200 text-slate-700",
  }[color];
  return (
    <div className={`border rounded-xl p-4 ${c}`}>
      <div className="text-[10px] uppercase font-bold tracking-wider opacity-80">{label}</div>
      <div className={`font-bold mt-1 ${big ? "text-3xl" : "text-xl"}`}>{value}</div>
    </div>
  );
}

function ManualModal({ title, color, form, setForm, onClose, onSubmit, warn }) {
  const btn =
    color === "emerald"
      ? "bg-emerald-600 hover:bg-emerald-700 text-white"
      : "bg-red-600 hover:bg-red-700 text-white";
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl p-5 w-full max-w-md shadow-2xl">
        <h3 className="text-lg font-bold text-slate-800 mb-3">{title}</h3>
        {warn && (
          <div className="bg-red-50 border border-red-200 rounded px-3 py-2 text-xs text-red-700 mb-3 flex gap-2 items-start">
            <AlertCircle className="w-4 h-4 mt-0.5" />
            {warn}
          </div>
        )}
        <div className="space-y-3">
          <Input
            type="number"
            placeholder="Amount"
            value={form.amount}
            onChange={(e) => setForm({ ...form, amount: e.target.value })}
            data-testid="manual-amount-input"
          />
          <Textarea
            placeholder="Reason*"
            rows={2}
            value={form.reason}
            onChange={(e) => setForm({ ...form, reason: e.target.value })}
            data-testid="manual-reason-input"
          />
          <Input
            placeholder="Reference (UTR / Note)"
            value={form.reference}
            onChange={(e) => setForm({ ...form, reference: e.target.value })}
            data-testid="manual-reference-input"
          />
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={onSubmit} className={btn} data-testid="manual-submit-btn">
            Confirm
          </Button>
        </div>
      </div>
    </div>
  );
}
